import { useAuth } from '../contexts/AuthContext';

/**
 * Feature Flags Configuration
 * This array should match the BETA_FEATURES array in the backend
 * (server/src/utils/featureFlags.js)
 *
 * When you add a feature here, only users with role 'beta_tester' (Team Member) or 'admin'
 * will see it. Remove it when ready for general release.
 *
 * Example: ['achievements', 'advanced_stats', 'team_tournaments']
 */
const BETA_FEATURES = [
  // Add feature names here
];

/**
 * Hook to check if user has access to a specific feature
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} - Whether user has access to the feature
 *
 * @example
 * function UserProfile() {
 *   const hasAchievements = useFeatureFlag('achievements');
 *
 *   return (
 *     <>
 *       <ProfileStats />
 *       {hasAchievements && <AchievementsBadge />}
 *     </>
 *   );
 * }
 */
export function useFeatureFlag(featureName) {
  const { user } = useAuth();

  if (!user) return false;

  // Admins have access to everything
  if (user.role === 'admin') return true;

  // If feature is not in beta list, everyone has access
  if (!BETA_FEATURES.includes(featureName)) {
    return true;
  }

  // Team members have access to beta features
  if (user.role === 'beta_tester') {
    return true;
  }

  // Standard users don't have access to beta features
  return false;
}

/**
 * Hook to check if user is an admin
 * @returns {boolean} - Whether user has admin role
 *
 * @example
 * function Navbar() {
 *   const isAdmin = useIsAdmin();
 *
 *   return (
 *     <nav>
 *       <Link to="/">Home</Link>
 *       {isAdmin && <Link to="/admin">Admin Panel</Link>}
 *     </nav>
 *   );
 * }
 */
export function useIsAdmin() {
  const { user } = useAuth();
  return user?.role === 'admin';
}

/**
 * Hook to check if user is a team member (includes admins)
 * @returns {boolean} - Whether user has team member or admin role
 *
 * @example
 * function FeedbackButton() {
 *   const isTeamMember = useIsTeamMember();
 *
 *   if (!isTeamMember) return null;
 *
 *   return <button onClick={showFeedbackForm}>Report Bug</button>;
 * }
 */
export function useIsTeamMember() {
  const { user } = useAuth();
  return user?.role === 'beta_tester' || user?.role === 'admin';
}

// Backward compatibility alias
export const useIsBetaTester = useIsTeamMember;

/**
 * Hook to get user's role
 * @returns {string} - User's role ('standard', 'beta_tester', or 'admin')
 */
export function useUserRole() {
  const { user } = useAuth();
  return user?.role || 'standard';
}
